<template>
    <div id="rx-transfer-body">
        <b-row>
            <b-col>
                <b-row v-if="editingDisabled">
                    <b-col>
                        <b>Requested By:</b> {{ transfer.requestedBy }} <br />
                        <b>Requested On:</b> {{ convertDate(transfer.timeStamp) }}<br />
                        <b>Status:</b> {{ getRxTransferEnum(transfer.transferStatus) }}<br />
                        <a v-if="transfer.transferStatusSetBy"><b>Status Since:</b>
                            {{ convertDate(transfer.transferStatusDate) }}<br /></a>
                        <a v-if="transfer.transferStatusSetBy"><b>Status Set By:</b> {{
                            transfer.transferStatusSetBy
                        }}</a>
                    </b-col>
                </b-row>
                <b-row class="mt-2">
                    <b-col>
                        <b-form-radio :disabled="editingDisabled" v-model="transfer.inbound" :value="true" inline>
                            Inbound
                        </b-form-radio>
                        <b-form-radio :disabled="editingDisabled" v-model="transfer.inbound" :value="false" inline>
                            Outbound
                        </b-form-radio>
                    </b-col>
                </b-row>
                <b-row v-if="!editingDisabled">
                    <b-col class="mt-2">
                        <b-badge v-if="pharmacySupportsRxTransfer" variant="primary" class="pull-right">
                            <i class="fa fa-bolt"></i>
                            Supports Electronic RxTransfer
                        </b-badge>
                        <b-badge v-if="isTransferToSelf" variant="warning" class="pull-right mr-2">
                            <i class="fa fa-exclamation-triangle"></i>
                            Cannot transfer to your own pharmacy
                        </b-badge>
                        <search-component v-model="pharmacy" :id="pharmacy && pharmacy.id"
                            search-u-r-l="/StoredPharmacy" :params="{ showOnlyActive: true }" label="Select Pharmacy *"
                            prepopulate create-new-object createNewLabel="Create New Pharmacy"
                            @createNewObject="newStoredPharma" @input="pharmacySelected" />

                        <search-component v-model="pharmacist" :disabled="!(pharmacy && pharmacy.id)"
                            :id="pharmacist && pharmacist.id"
                            :search-u-r-l="`/api/StoredPharmacist/GetAllPharmacistsByStore/${pharmacy.id}`"
                            get-by-id-u-r-l="/api/StoredPharmacist" label="Select Pharmacist" prepopulate
                            create-new-object clearable @createNewObject="newStoredPharmacist"
                            createNewLabel="Create New Pharmacist" @input="pharmacistSelected" />
                        <search-component ref="xferReason" v-model="transferReason"
                            :id="transferReason && transferReason.id" search-u-r-l="/RxTransfer/Reasons"
                            label="Transfer Reason" prepopulate @input="rxTransferReasonSelected" />
                        <search-component v-if="transfer.inbound" v-model="transferToStore"
                            :id="transferToStore && transferToStore.id" search-u-r-l="/Store" label="Transfer To Store *"
                            prepopulate />
                    </b-col>
                </b-row>
                <b-row v-if="editingDisabled">
                    <b-col>
                        <b-row>
                            <b-col>
                                <fg-input v-model="transfer.externalPharmacyName" label="Pharmacy" readonly />
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col cols="12">
                                <fg-input v-model="transfer.externalPharmacyPhone" label="Pharmacy Phone" readonly />
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col cols="12" class="mt-2">
                                <fg-input v-model="transfer.externalPharmacistName" label="Pharmacists" readonly />
                            </b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                                <fg-input v-model="transfer.rxTransferReasonName" label="Transfer Reason" readonly />
                            </b-col>
                        </b-row>
                    </b-col>
                </b-row>

                <hr>

                <b-row v-if="!editingDisabled">
                    <b-col cols="12">
                        <b-form-radio inline v-model="transfer.all" :value="false">Specific Prescriptions</b-form-radio>
                        <b-form-radio inline v-model="transfer.all" :value="true">All Prescriptions</b-form-radio>
                    </b-col>
                </b-row>

                <b-row v-if="!editingDisabled && !transfer.all">
                    <b-col cols="5">
                        <fg-input ref="drugName" v-model="newDrugName" label="Drug Name" maxlength="300" />
                    </b-col>
                    <b-col cols="3">
                        <fg-input ref="ndc" v-model="newDrugNDC" label="Drug NDC" maxlength="20" />
                    </b-col>
                    <b-col cols="2">
                        <fg-input v-model="newDrugRx" label="Rx Number" placeholder="12345678" maxlength="30" />
                    </b-col>
                    <b-col cols="2" class="mt-4">
                        <b-button size="sm" block @click="addToTransferContents" variant="success" inline>
                            Add
                        </b-button>
                    </b-col>
                </b-row>

                <hr>

                <b-table v-if="transfer.contents.length" :items="transfer.contents" :fields="fields">
                    <template #cell(goodToTransfer)="data" v-if="editingDisabled">
                        <b>{{ data.value == false ? 'Missing Pv1' : '' }}</b>
                    </template>
                    <template #cell(remove)="data" v-if="!editingDisabled">
                        <b-button size="sm" variant="danger" @click="removeDrugFromTransfer(data.item)">
                            Remove
                        </b-button>
                    </template>
                </b-table>

                <b-row>
                    <b-col>
                        <b>Notes:</b>
                        <NotesList ref="notes-list" :paging="false" :entity-type="EntityType.RxTransferQueue"
                            :entity-id="transfer.id" />
                    </b-col>
                </b-row>

                <b-row>
                    <b-col>
                        <b-form-textarea v-model="newNote" placeholder="Enter Note" />
                        <b-button v-if="editingDisabled" size="sm" variant="info" @click="saveNote">
                            Save Note
                        </b-button>
                    </b-col>
                </b-row>

                <b-row v-if="!editingDisabled" align-h="end" align-v="baseline" class="text-right">
                    <b-col>
                        <b-checkbox v-model="sendFax" v-show="!disableFax">Send Electronic/Fax</b-checkbox>
                    </b-col>
                    <b-col cols="auto">
                        <b-button variant="success" :disabled="!isFormComplete" @click="submitRxTransfer">
                            Submit
                        </b-button>
                    </b-col>
                </b-row>

                <b-row v-if="editingDisabled" align-h="end" align-v="baseline" class="text-right">
                    <b-col cols="auto">
                        <b-dropdown dropup text="Cancel" variant="danger">
                            <b-dropdown-item @click="changeRxTransferStatus(2, 'Not Transferable')">
                                Not Transferable
                            </b-dropdown-item>
                            <b-dropdown-item @click="changeRxTransferStatus(2, 'Insufficient Quantity')">
                                Insufficient Quantity
                            </b-dropdown-item>
                            <b-dropdown-item @click="changeRxTransferStatus(2, 'Contacting Doctor')">
                                Contacting Doctor
                            </b-dropdown-item>
                        </b-dropdown>
                        <b-dropdown dropup text="Change Status" variant="warning">
                            <b-dropdown-item @click="changeRxTransferStatus(0, null)">New</b-dropdown-item>
                            <b-dropdown-item @click="changeRxTransferStatus(1, null)">Open</b-dropdown-item>
                        </b-dropdown>
                        <b-button variant="success" :disabled="disableComplete"
                            @click="changeRxTransferStatus(3, null)">
                            Mark As Complete
                        </b-button>
                    </b-col>
                </b-row>

            </b-col>
        </b-row>
        <b-modal id="addNewPharmacy" hide-footer size="xl" no-close-on-backdrop title="Create New Pharmacy">
            <PharmacyDetailPage new-mode @close="$bvModal.hide('addNewPharmacy')" @created="onPharmacyCreate"
                :hide-title="true" :default-to-active="true" />
        </b-modal>
        <b-modal id="addNewPharmacist" hide-footer size="xl" no-close-on-backdrop title="Create New Pharmacist">
            <PharmacistDetailPage :disabled="!pharmacy || !pharmacy.id" :pharmacy-id="pharmacy && +pharmacy.id" new-mode
                @close="$bvModal.hide('addNewPharmacist')" @created="onPharmacistCreate" :hide-title="true"
                :default-to-active="true" />
        </b-modal>
    </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch, Ref } from 'vue-property-decorator';
import axios from 'axios';
import { Patient } from '@/models/Patient';
import { RxTransfer, RxTransferContents, RxTransferReason, transferStatus } from '@/models/RxTransfer';
import { DatePicker } from 'element-ui';
import { Note } from '@/models/Note';
import NotesList from '@/components/Note/NotesList.vue';
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import moment from 'moment';
import SearchComponent from "@/components/SearchComponent.vue";
import { PharmacyRecord } from "@/models/Pharmacy";
import { PharmacistRecord } from "@/models/Pharmacist";
import PharmacyDetailPage from "@/pages/Pharmacy/PharmacyDetailPage.vue";
import PharmacistDetailPage from "@/pages/Pharmacist/PharmacistDetailPage.vue";
import { EntityType } from '@/models/EntityType';
import { Store } from '@/models/Store';

const transferToStoreCacheKey = "rx-transfer-modal-transfer-to-store-id";

@Component({
    name: "RxTransferModalComponent",
    components: {
        PharmacistDetailPage,
        PharmacyDetailPage,
        SearchComponent,
        [DatePicker.name]: DatePicker,
        NotesList,
    },
})
export default class RxTransferModalComponent extends Vue {
    @Prop() patient!: Patient;
    @Prop() TransferIdDetails!: number;
    public startingContents: RxTransferContents[] = [];
    protected notes: Note[] = [];
    private transfer: RxTransfer = {
        contents: [...this.startingContents],
        notes: [...this.notes],
        inbound: undefined,
        all: false,
    } as RxTransfer;
    private newDrugName: string = "";
    private newDrugRx: string | null = null;
    private newDrugNDC: string = "";
    private newNote: Note = null as unknown as Note;
    private pharmacy: PharmacyRecord = new PharmacyRecord();
    private pharmacist: PharmacistRecord = new PharmacistRecord();
    private transferReason: RxTransferReason = new RxTransferReason();
    @Ref("xferReason") xferReason?: SearchComponent;
    private transferToStore: Store = new Store();
    private pharmacySupportsRxTransfer: boolean | null = null;
    private isTransferToSelf: boolean = false;

    protected sendFax: boolean = false;

    protected EntityType = EntityType;

    protected fields: any[] = [
        { key: "goodToTransfer", label: "" },
        { key: "medicationName", label: "Drug" },
        { key: "ndc", label: "NDC" },
        "rxNumber",
        { key: "remove", label: "" },
    ];

    @Watch("transferToStore")
    handleTransferToStoreChange() {
        localStorage.setItem(transferToStoreCacheKey, this.transferToStore.id.toString());
    }

    @Watch('startingContents', { deep: true })
    startingContentsUpdate(_newVal: any, _oldVal: any) {
        this.populateWithStartingContents();
    }

    @Watch("pharmacy")
    async handlePharmacyChange(newValue: PharmacyRecord, oldValue: PharmacyRecord) {
        try {
            if (!newValue.id || newValue.id === oldValue.id) return;
            this.pharmacySupportsRxTransfer = null;
            this.isTransferToSelf = false;
            const resp = await axios.get(`/RxTransfer/TransferDirectoryLookup/${this.pharmacy.id}`);
            this.pharmacySupportsRxTransfer = true;
            this.pharmacy = resp.data;

            await this.checkIfTransferToSelf();
        } catch {
            this.pharmacySupportsRxTransfer = false;
            this.isTransferToSelf = false;
        }
    }

    populateWithStartingContents() {
        if (!this.transfer.contents) this.transfer.contents = [];
        for (const startingContent of this.startingContents) {
            this.newDrugName = startingContent.medicationName;
            this.newDrugRx = startingContent.rxNumber;
            this.newDrugNDC = startingContent.ndc;
            this.addToTransferContents();
        }
        if (this.transfer.inbound === undefined) {
            this.transfer.inbound = false;
        }
    }

    addToTransferContents() {
        const contents = {
            medicationName: this.newDrugName,
            rxNumber: this.newDrugRx,
            ndc: this.newDrugNDC,
        } as RxTransferContents;

        this.transfer.contents.push(contents);
        this.newDrugRx = null;
        this.newDrugName = "";
        this.newDrugNDC = "";
    }

    created() {
        if (this.TransferIdDetails) {
            this.getExistingTransferDetails();
        }
        this.transferToStore = Object.assign(new Store(), { id: localStorage.getItem(transferToStoreCacheKey) });
    }

    getExistingTransferDetails() {
        axios.get(`/RxTransfer/${this.TransferIdDetails}/GetByTransferId`)
            .then(response => {
                this.transfer = response.data;
            });
    }

    removeDrugFromTransfer(drug: RxTransferContents) {
        this.transfer.contents = this.transfer.contents.filter(function (el) {
            return el.medicationName != drug.medicationName;
        });
    }

    get isFormComplete(): boolean {
        if (!this.transfer) {
            return false;
        }
        if (!this.transfer.all && !this.transfer.contents.length) {
            return false;
        }
        if (!this.pharmacy?.name?.length) {
            return false;
        }
        if (!this.transfer.externalPharmacyName || this.transfer.externalPharmacyName == "") {
            return false;
        }
        if (!this.transfer.externalPharmacyPhone || this.transfer.externalPharmacyPhone == "") {
            return false;
        }
        if (this.transfer.inbound && !this.transferToStore?.id) {
            return false;
        }
        if (this.isTransferToSelf && this.transfer.inbound && this.sendFax) {
            return false;
        }
        return this.transfer.inbound != undefined;
    }

    get disableComplete() {
        return this.transfer.contents.some(x => x.goodToTransfer == false);
    }

    get disableFax() {
        return !(this.transfer.inbound && this.pharmacy?.faxNumber?.length > 0);
    }

    get editingDisabled() {
        return !!this.TransferIdDetails;
    }

    async saveNote() {
        if (!this.newNote) return;

        try {
            await axios.post(`/RxTransfer/${this.TransferIdDetails}/SaveNote`, JSON.stringify(this.newNote));
            this.getExistingTransferDetails();
            this.newNote = null as unknown as Note;
            this.refreshNotesList();
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        }
    }

    pharmacySelected() {
        this.transfer.externalPharmacyId = this.pharmacy.id;
        this.transfer.externalPharmacyName = this.pharmacy.name;
        this.transfer.externalPharmacyPhone = this.pharmacy.phoneNumber;
    }

    pharmacistSelected() {
        if (isNaN(this.pharmacy.id)) return;
        this.transfer.externalPharmacistName = `${this.pharmacist?.lastName}, ${this.pharmacist?.firstName}`;
        if (!this.pharmacist) this.transfer.externalPharmacistName = "";
    }

    rxTransferReasonSelected() {
        if (isNaN(this.transferReason.id)) return;
        this.transfer.rxTransferReasonId = this.transferReason.id;
    }

    submitRxTransfer() {
        if (this.newNote) {
            const addedNote: Note = { body: this.newNote } as unknown as Note;
            this.transfer.notes.push(addedNote);
        }
        // this.transfer.inbound = this.transfer.inbound as unknown as string === 'true';
        this.transfer.rxTransferReasonId = this.transferReason.id;
        this.transfer.externalPharmacyFax = this.pharmacy.faxNumber;
        this.transfer.patientId = Number(this.patient.id);
        this.transfer.transferToStoreId = this.transferToStore.id;

        axios.post(`/api/RxTransfer/${this.patient.id}/AddToRxTransferQueue`,
            this.transfer,
            { params: { sendFax: this.transfer.inbound && this.sendFax } })
            .then(_response => {
                this.$notification(NotificationOptions.notificationOptionsPreset("Transfer Created Successfully", NotificationOptions.NotificationTypes.success));
                this.$emit("complete");
            })
            .catch((err) => {
                this.$notification(NotificationOptions.notificationOptionsPreset("Failed To Create Transfer", NotificationOptions.NotificationTypes.danger));
                this.$notification(NotificationOptions.error(err));
            });
    }

    changeRxTransferStatus(statusId: number, reason: string | null) {
        if (!reason) {
            reason = null;
        }
        axios.post(`/RxTransfer/${this.TransferIdDetails}/${this.transfer.patientId}/ChangeStatus/${statusId}/${reason}`)
            .then(_response => {
                this.saveNote();
                this.$notification(NotificationOptions.notificationOptionsPreset("Change Saved", NotificationOptions.NotificationTypes.success));
                this.$emit("complete");
            })
            .catch(error => {
                this.$notification(NotificationOptions.error(error));
            });
    }

    convertDate(date: string | Date) {
        return moment.utc(date).format("L");
    }

    getRxTransferEnum(status: number) {
        return transferStatus[status];
    }

    newStoredPharma() {
        this.pharmacy = new PharmacyRecord();
        this.$bvModal.show("addNewPharmacy");
    }

    newStoredPharmacist() {
        this.pharmacist = new PharmacistRecord();
        this.$bvModal.show("addNewPharmacist");
    }

    onPharmacyCreate(newPharma: PharmacyRecord) {
        this.pharmacy = newPharma;
        this.$bvModal.hide("addNewPharmacy");
    }

    onPharmacistCreate(newPharmacist: PharmacistRecord) {
        this.pharmacist = newPharmacist;
        this.$bvModal.hide("addNewPharmacist");
        this.$nextTick(() => {
            setTimeout(() => {
                this.xferReason?.focus();
            }, 300);
        });
    }

    @Ref("notes-list") protected notesList!: NotesList;

    refreshNotesList() {
        this.notesList.loadNotes();
    }

    async checkIfTransferToSelf() {
        try {
            if (!this.pharmacy?.ncpdp && !this.pharmacy?.npiNumber) {
                this.isTransferToSelf = false;
                return;
            }

            const storesResp = await axios.get('/Store');
            const stores = storesResp.data;

            if (!stores || !Array.isArray(stores)) {
                this.isTransferToSelf = false;
                return;
            }

            // Check if selected pharmacy matches any of our stores by NCPDP or NPI
            this.isTransferToSelf = stores.some((store: any) =>
                (this.pharmacy.ncpdp && store.ncpdp &&
                 this.pharmacy.ncpdp.trim().toLowerCase() === store.ncpdp.trim().toLowerCase()) ||
                (this.pharmacy.npiNumber && store.npiNumber &&
                 this.pharmacy.npiNumber.trim().toLowerCase() === store.npiNumber.trim().toLowerCase())
            );
        } catch (error) {
            console.error('Error checking if transfer to self:', error);
            this.isTransferToSelf = false;
        }
    }
}
</script>

<style scoped></style>
